import React, {
    useEffect,
    useState,
    useMemo,
} from "react";

import api from "../api/api";

import Swal from "sweetalert2";

import {
    Layout,
    Card,
    Typography,
    Input,
    Row,
    Col,
    Tag,
    Space,
    Spin,
    Empty,
    Avatar,
    message,
    Divider,
    Badge,
    Button,
} from "antd";

import {
    SearchOutlined,
    FileProtectOutlined,
    UserOutlined,
    SafetyCertificateOutlined,
    SendOutlined,
    CheckCircleOutlined,
} from "@ant-design/icons";

import { useAcademicYear } from "../AcademicYearContext";

import StudentComplainChart from "./StudentComplainChart";

const { Title, Text } = Typography;

const ADMIN_ROLES = [
    "admin",
    "dean",
    "vpi",
    "vpa",
];

const TEACHER_ROLES = [
    "teacher",
];

const normalizeRole = (role) => {
    return (role || "").trim().toLowerCase();
};

const getStoredUser = () => {
    const raw = localStorage.getItem("user");

    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

const ComplaintUnit = () => {
    const {
        selectedYear,
        initialized,
    } = useAcademicYear();

    const [currentUser, setCurrentUser] = useState(null);

    const [currentUserId, setCurrentUserId] = useState(null);

    const [userType, setUserType] = useState("");
    const [userLoaded, setUserLoaded] = useState(false);
    const [complaints, setComplaints] = useState([]);

    const [periods, setPeriods] = useState([]);

    const [gradeClasses, setGradeClasses] = useState([]);

    const [selectedPeriod, setSelectedPeriod] = useState(null);

    const [selectedGradeClass, setSelectedGradeClass] = useState(null);

    const [loading, setLoading] = useState(false);

    const [sendingComplaintId, setSendingComplaintId] = useState(null);

    const [search, setSearch] = useState("");
    

useEffect(() => {

    const user = getStoredUser();

    if (user) {

        setCurrentUser(user);

        setCurrentUserId(user.id);

        setUserType(
            normalizeRole(
                user.user_type || user.role
            )
        );

    }

    setUserLoaded(true);

}, []);

    const isAdmin = useMemo(() => {
        return ADMIN_ROLES.includes(userType);
    }, [userType]);

    const isTeacher = useMemo(() => {
        return TEACHER_ROLES.includes(userType);
    }, [userType]);

    const canAccess = useMemo(() => {
        return isAdmin || isTeacher;
    }, [
        isAdmin,
        isTeacher,
    ]);

    const canViewChart = useMemo(() => {
        return isAdmin;
    }, [isAdmin]);

    const canSendToParent = useMemo(() => {
        return isAdmin;
    }, [isAdmin]);

    const visibleComplaints = useMemo(() => {
        if (isAdmin) {
            return complaints;
        }

        return complaints.filter((item) => {
            if (item.poster === currentUserId) {
                return true;
            }

            if (item.poster_id === currentUserId) {
                return true;
            }

            if (item.poster?.id === currentUserId) {
                return true;
            }

            return false;
        });
    }, [
        complaints,
        currentUserId,
        isAdmin,
    ]);
    const fetchPeriods = async () => {
        try {
            const response = await api.get("/api/users/periods/");

            const data = Array.isArray(response.data)
                ? response.data
                : response.data?.results || [];

            setPeriods(data);
        } catch (error) {
            console.error("Unable to load periods:", error);
            message.error("Unable to load periods.");
        }
    };

    const fetchGradeClasses = async () => {
        if (!selectedYear?.id) {
            return;
        }

        try {
            const response = await api.get(
                "/api/users/grade-classes/",
                {
                    params: {
                        academic_year: selectedYear.id,
                    },
                }
            );

            const data = Array.isArray(response.data)
                ? response.data
                : response.data?.results || [];

            setGradeClasses(data);
        } catch (error) {
            console.error(
                "Unable to load grade classes:",
                error
            );

            message.error(
                "Unable to load grade classes."
            );
        }
    };

    const fetchComplaints = async () => {
        if (!selectedYear?.id) {
            setComplaints([]);
            return;
        }

        try {
            setLoading(true);

            const response = await api.get(
                "/complaint-unit/",
                {
                    params: {
                        academic_year: selectedYear.id,
                    },
                }
            );

            const data = Array.isArray(response.data)
                ? response.data
                : response.data?.results || [];

            setComplaints(data);
        } catch (error) {
            console.error(
                "Unable to load complaint records:",
                error
            );

            message.error(
                "Unable to load complaint records."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (
            !initialized ||
            !selectedYear?.id ||
            !canAccess
        ) {
            return;
        }

        fetchComplaints();
        fetchPeriods();
        fetchGradeClasses();
    }, [
        initialized,
        selectedYear?.id,
        canAccess,
    ]);

    const filteredComplaints = useMemo(() => {
        const query = search
            .trim()
            .toLowerCase();

        return visibleComplaints.filter((item) => {
            if (!query) {
                return true;
            }

            return (
                item.title
                    ?.toLowerCase()
                    .includes(query) ||

                item.description
                    ?.toLowerCase()
                    .includes(query) ||

                item.student_full_name
                    ?.toLowerCase()
                    .includes(query) ||

                item.poster_name
                    ?.toLowerCase()
                    .includes(query) ||

                item.poster_username
                    ?.toLowerCase()
                    .includes(query) ||

                item.grade_class_name
                    ?.toLowerCase()
                    .includes(query) ||

                item.period_name
                    ?.toLowerCase()
                    .includes(query)
            );
        });
    }, [
        visibleComplaints,
        search,
    ]);

if (!userLoaded) {

    return (
        <div
            style={{
                padding:40,
                textAlign:"center"
            }}
        >
            <Spin size="large"/>
        </div>
    );

}


if (!canAccess) {

    return (
        <Typography.Text type="danger">
            You do not have permission to access the Complaint Unit.
        </Typography.Text>
    );

}
    const sendToParent = async (complaintId) => {
        const result = await Swal.fire({
            title: "Send Complaint to Parent?",
            text: "Are you sure you want to send this complaint to the parent?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Send It",
            cancelButtonText: "Cancel",
            reverseButtons: true,
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            setSendingComplaintId(complaintId);

            const response = await api.patch(
                `/complaint-unit/${complaintId}/send-to-parent/`
            );

            setComplaints((previous) =>
                previous.map((complaint) =>
                    complaint.id === complaintId
                        ? response.data
                        : complaint
                )
            );

            Swal.fire({
                icon: "success",
                title: "Complaint Sent",
                text: "The complaint has been successfully sent to the parent.",
                confirmButtonText: "OK",
            });
        } catch (error) {
            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Unable to Send",
                text: "The complaint could not be sent to the parent.",
                confirmButtonText: "OK",
            });
        } finally {
            setSendingComplaintId(null);
        }
    };

    return (
        <Layout
            style={{
                background: "#e5e7eb",
                minHeight: "100%",
                padding: 20,
            }}
        >
            <Card
                style={{
                    maxWidth: 1400,
                    width: "100%",
                    margin: "auto",
                    background: "#111827",
                    borderRadius: 20,
                }}
            >
                <Space
                    direction="vertical"
                    style={{
                        width: "100%",
                    }}
                >
                    <Title
                        level={2}
                        style={{
                            color: "#ffffff",
                            textAlign: "center",
                            marginBottom: 0,
                        }}
                    >
                        Complaint Investigation Unit
                    </Title>

                    <Text
                        style={{
                            color: "#d1d5db",
                            display: "block",
                            textAlign: "center",
                        }}
                    >
                        {isAdmin
                            ? "Administrative review and monitoring of all student complaints."
                            : "View and monitor complaints you have submitted."}
                    </Text>

                    {selectedYear && (
                        <div
                            style={{
                                textAlign: "center",
                                marginTop: 10,
                            }}
                        >
                            <Tag
                                color="volcano"
                                style={{
                                    fontSize: 15,
                                    fontWeight: 700,
                                    padding: "6px 14px",
                                }}
                            >
                                Academic Year: {selectedYear.name}
                            </Tag>
                        </div>
                    )}

                    <Card
                        style={{
                            marginTop: 20,
                            borderRadius: 18,
                        }}
                    >
                        <Input
                            size="large"
                            placeholder="Search complaint records..."
                            prefix={<SearchOutlined />}
                            value={search}
                            onChange={(event) =>
                                setSearch(event.target.value)
                            }
                        />

                        {canViewChart && (
                            <StudentComplainChart
                                complaints={filteredComplaints}
                                periods={periods}
                                gradeClasses={gradeClasses}
                                selectedPeriod={selectedPeriod}
                                setSelectedPeriod={setSelectedPeriod}
                                selectedGradeClass={selectedGradeClass}
                                setSelectedGradeClass={
                                    setSelectedGradeClass
                                }
                            />
                        )}

                        <Divider />

                        {loading ? (
                            <div
                                style={{
                                    textAlign: "center",
                                    padding: 60,
                                }}
                            >
                                <Spin size="large" />
                            </div>
                        ) : filteredComplaints.length === 0 ? (
                            <Empty
                                description={
                                    isAdmin
                                        ? `No complaints found for ${
                                              selectedYear?.name ||
                                              "the selected academic year"
                                          }.`
                                        : "You have not submitted any complaints."
                                }
                            />
                        ) : (
                            <Row gutter={[20, 20]}>
                                {filteredComplaints.map((item) => (
                                    <Col
                                        xs={24}
                                        sm={12}
                                        lg={8}
                                        key={item.id}
                                    >
                                        <Card
                                            hoverable
                                            style={{
                                                height: "100%",
                                                borderRadius: 18,
                                                borderTop:
                                                    "5px solid #6b0f1a",
                                            }}
                                        >
                                            <Space
                                                direction="vertical"
                                                size="middle"
                                                style={{
                                                    width: "100%",
                                                }}
                                            >
                                                <Row
                                                    justify="space-between"
                                                    align="middle"
                                                >
                                                    <Col>
                                                        <Space>
                                                            <Avatar
                                                                size={46}
                                                                icon={
                                                                    <UserOutlined />
                                                                }
                                                                style={{
                                                                    background:
                                                                        "#111827",
                                                                }}
                                                            />

                                                            <div>
                                                                <Text
                                                                    strong
                                                                >
                                                                    {
                                                                        item.student_full_name
                                                                    }
                                                                </Text>

                                                                <br />

                                                                <Text
                                                                    type="secondary"
                                                                >
                                                                    {item.grade_class_name ||
                                                                        item.grade_class ||
                                                                        "N/A"}
                                                                </Text>
                                                            </div>
                                                        </Space>
                                                    </Col>

                                                    <Col>
                                                        <Badge
                                                            status={
                                                                item.sent_to_parent
                                                                    ? "success"
                                                                    : "processing"
                                                            }
                                                            text={
                                                                item.sent_to_parent
                                                                    ? "Sent to Parent"
                                                                    : "Under Review"
                                                            }
                                                        />
                                                    </Col>
                                                </Row>

                                                <Divider
                                                    style={{
                                                        margin: "6px 0",
                                                    }}
                                                />

                                                <Title
                                                    level={5}
                                                    style={{
                                                        margin: 0,
                                                    }}
                                                >
                                                    <FileProtectOutlined />{" "}
                                                    {item.title}
                                                </Title>

                                                <Text>
                                                    {item.description}
                                                </Text>

                                                <Space wrap>
                                                    <Tag color="red">
                                                        Official Complaint
                                                    </Tag>

                                                    {item.period_name && (
                                                        <Tag color="blue">
                                                            {
                                                                item.period_name
                                                            }
                                                        </Tag>
                                                    )}

                                                    {item.created_at && (
                                                        <Tag color="gold">
                                                            {new Date(
                                                                item.created_at
                                                            ).toLocaleDateString()}
                                                        </Tag>
                                                    )}
                                                </Space>

                                                <Divider
                                                    style={{
                                                        margin: "8px 0",
                                                    }}
                                                />

                                                <Text type="secondary">
                                                    <strong>
                                                        Posted By:
                                                    </strong>{" "}
                                                    {item.poster_name ||
                                                        item.poster_username}
                                                </Text>

                                                {canSendToParent && (
                                                    <>
                                                        <Divider
                                                            style={{
                                                                margin:
                                                                    "10px 0",
                                                            }}
                                                        />

                                                        {item.sent_to_parent ? (
                                                            <Tag
                                                                color="green"
                                                                icon={
                                                                    <CheckCircleOutlined />
                                                                }
                                                                style={{
                                                                    width:
                                                                        "100%",
                                                                    textAlign:
                                                                        "center",
                                                                    padding:
                                                                        "8px 12px",
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                Sent to Parent
                                                            </Tag>
                                                        ) : (
                                                            <Button
                                                                block
                                                                type="primary"
                                                                icon={
                                                                    <SendOutlined />
                                                                }
                                                                loading={
                                                                    sendingComplaintId ===
                                                                    item.id
                                                                }
                                                                onClick={() =>
                                                                    sendToParent(
                                                                        item.id
                                                                    )
                                                                }
                                                                style={{
                                                                    background:
                                                                        "#6b0f1a",
                                                                    borderColor:
                                                                        "#6b0f1a",
                                                                }}
                                                            >
                                                                Send to Parent
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                            </Space>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </Card>
                </Space>
            </Card>
        </Layout>
    );
};

export default ComplaintUnit;